import React, { useEffect, useState } from "react";
import { ButtonGroup, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { LevelPieChart } from "./charts-metrics/LevelPieChart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from "html2canvas";
import { ProgramTable } from "./charts-metrics/ProgramTable";
import { fetchPrograms, fetchReportPrograms } from "../../services/httpClient";
import { signal } from "@preact/signals-react";
import { CSVLink } from "react-csv";
import { getCurrentDateTime } from "../../helpers/date";
import { wait } from "../../helpers/loading";

export const showPrograms = signal(false);

const ProgramsTab = ({ buildPDF, canvas, setCanvas, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reportRunning, setReportRunning] = useState<boolean>(false);
  const [programData, setProgramData] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levelsUsed, setLevelsUsed] = useState<boolean>(false);
  const [selectedProgramTab, setSelectedProgramTab] = useState<string | number | null>(0);

  const defaultCSVHeaders = [
    { label: "Program ID", key: "program_id", hide: true },
    { label: "Program", key: "program" },
    ...(levelsUsed
      ? [
          { label: "Level ID", key: "level_id", hide: true },
          { label: "Level", key: "level" },
        ]
      : []),
    { label: "Case ID", key: "id", hide: true },
    { label: "Case", key: "case_name" },
    { label: "Name", key: "name" },
  ];

  useEffect(() => {
    getProgramData();
  }, []);

  useEffect(() => {
    if (programData) getPrograms();
  }, [programData]);

  const getPrograms = () => {
    fetchPrograms()
      .then((res: any) => {
        const programs = res[0]?.data ?? [];
        showPrograms.value = programs.length > 0 ? true : false;
        const flattenedProgs = programs.flatMap((prog: any) => {
          const levels = prog.programLevels ?? [];

          // Add level name to each programData entry
          programData.forEach((d: any) => {
            if (d.program_id === prog.id) {
              const level = levels.find((l: any) => l.id === d.level_id);
              d.level = level?.name;
              if (level) {
                setLevelsUsed(true);
              }
            }
          });

          const levelEntries = levels.map((level: any) => {
            const levelCount = programData.filter((d: any) => d.level_id === level.id).length;

            return {
              ...prog,
              id: `${prog.id}-${level.id}`,
              level_id: level.id,
              program_id: prog.id,
              name: `${prog.name} - ${level.name} (${levelCount})`,
              key: `${prog.id}-${level.id}`,
            };
          });

          // Always include the base program entry
          const baseCount = programData.filter((d: any) => d.program === prog.name).length;
          const baseProgramEntry = {
            ...prog,
            id: prog.id,
            program_id: prog.id,
            name: `${prog.name} (${baseCount})`,
            key: prog.id,
          };
          return [baseProgramEntry, ...levelEntries];
        });

        setPrograms(flattenedProgs);
      })
      .catch((err: any) => console.log(err))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getProgramData = () => {
    fetchReportPrograms()
      .then((res: any) => {
        const progData = res[0]?.data ?? [];
        setProgramData(progData);
      })
      .catch((err: any) => console.log(err));
  };

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const onClickDownloadReport = async () => {
    setSelectedProgramTab(0);
    await wait(500); //wait for state to be set
    setReportRunning(true);
    const reportSection = document.getElementById("programs-section");
    const reportTitle = document.getElementById("programs-title");
    const programsFilters = document.getElementById("programs-filters");
    const pagination = document.getElementById("programs-pagination");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }

      if (programsFilters) {
        programsFilters.style.visibility = "hidden";
        programsFilters.style.display = "none";
      }

      if (pagination) {
        pagination.style.visibility = "hidden";
        pagination.style.display = "none";
      }

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }

      if (programsFilters) {
        programsFilters.style.visibility = "visible";
        programsFilters.style.display = "flex";
      }

      if (pagination) {
        pagination.style.visibility = "visible";
        pagination.style.display = "flex";
      }
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  return (
    <div id="programs-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="programs-title" className="mb-3">
            <Col className="col-8">
              <h4 className="mb-0 app-header-text">Programming</h4>
            </Col>
            <Col className="col-4 text-end">
              {!iPad && (
                <Dropdown as={ButtonGroup}>
                  <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button">
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    Downloads
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="super-colors">
                    <Dropdown.Item eventKey="1" onClick={() => onClickDownloadReport()}>
                      Report
                    </Dropdown.Item>
                    <Dropdown.Item
                      eventKey="2"
                      as={CSVLink}
                      data={programData ? programData : [{}]}
                      headers={defaultCSVHeaders}
                      filename={`programming_${getCurrentDateTime()}.csv`}
                      target="_blank"
                    >
                      Data Export
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Col>
          </Row>
          {programData?.length > 0 ? (
            <>
              <Row id="programs-pie" className="justify-content-start mb-3">
                <Col xs={10}>
                  <LevelPieChart
                    key={selectedProgramTab}
                    data={programData}
                    selectedProgramTab={selectedProgramTab}
                    setSelectedProgramTab={setSelectedProgramTab}
                  />
                </Col>
              </Row>
              <ProgramTable data={programData} programList={programs} />
            </>
          ) : (
            <p className="mt-2">No Program Records</p>
          )}
        </>
      )}
    </div>
  );
};

export default ProgramsTab;
